import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PlusIcon } from 'lucide-react';
import { FiArrowLeft, FiSearch } from 'react-icons/fi';
import { GrStorage } from 'react-icons/gr';
import { useLocation, useNavigate } from 'react-router-dom';
import { Drawer } from '../../../components/Drawer';
import { Button, Label, Tooltip } from 'flowbite-react';
import Input from '../../../components/input';
import BucketItem from '../../../components/Atoms/BucketItem';
import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';
import { toast } from 'react-toastify';
import { BsInfoCircle } from 'react-icons/bs';
import CardSkeleton from '../../../components/Skeletons/CardSkeleton';

// ─── Tier & billing config ────────────────────────────────────────────────────

export const tierList = [
  { name: 'Basic', gb: 150, monthlyPrice: 5000 },
  { name: 'Standard', gb: 300, monthlyPrice: 9000 },
  { name: 'Advanced', gb: 500, monthlyPrice: 13500 },
];

const billingCycles = [
  { label: 'Monthly', key: 'monthly', multiplier: 1, months: 1 },
];

function formatPrice(amount) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function calcPrice(tier, cycle) {
  if (!tier) return null;
  return tier.monthlyPrice * cycle.multiplier;
}

// ─── Tier Card ────────────────────────────────────────────────────────────────

function TierCard({ tier, selected, cycle, onSelect }) {
  const price = calcPrice(tier, cycle);
  const isSelected = selected?.name === tier.name;

  return (
    <button
      type="button"
      onClick={() => onSelect(tier)}
      className={`
        relative w-full rounded-xl border-2 p-4 text-left transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-[#1da1f1]/40
        ${
          isSelected
            ? 'border-[#1da1f1] bg-[#1da1f1]/5 shadow-md'
            : 'border-gray-200 bg-white hover:border-[#1da1f1]/50 hover:shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:hover:border-[#1da1f1]/50'
        }
      `}
    >
      {/* Selected badge */}
      {isSelected && (
        <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[#1da1f1]">
          <svg
            className="h-3 w-3 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </span>
      )}

      <div>
        {/* Tier name */}
        <p
          className={`text-sm font-semibold ${isSelected ? 'text-[#1da1f1]' : 'text-gray-700 dark:text-gray-200'}`}
        >
          {tier.name}
        </p>

        {/* Storage */}
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {tier.gb} GB storage
        </p>

        {/* Price */}
        <div className="mt-2">
          <span
            className={`text-lg font-bold ${isSelected ? 'text-[#1da1f1]' : 'text-gray-800 dark:text-gray-100'}`}
          >
            {formatPrice(price)}
          </span>
          <span className="ml-1 text-xs text-gray-400">
            /{cycle.label.toLowerCase()}
          </span>
        </div>
      </div>
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const BucketStorages = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const projectId = selectedProject?.id;

  const [isOpen, setIsOpen] = useState(false);
  const defaultValues = { name: '', private: false };
  const [newBucket, setNewBucket] = useState(defaultValues);
  const [isFetchingBuckets, setIsFetchingBuckets] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [buckets, setBuckets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const isBucketLimitReached = buckets.length >= 25;

  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState(billingCycles[0]);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setNewBucket((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const hasBuckets = buckets && buckets.length > 0;

  const handleFetchBucketStorages = useCallback(async () => {
    setIsFetchingBuckets(true);
    try {
      const { data } = await apiEndPoints.fetchBucketStorage(projectId);
      setBuckets(data?.storage_details);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching buckets');
    } finally {
      setIsFetchingBuckets(false);
    }
  }, [projectId]);

  const handleCreateBucket = useCallback(
    async (e) => {
      e.preventDefault();
      if (!newBucket.name || !selectedTier) return;
      setIsCreating(true);
      try {
        await apiEndPoints.createBucketStorage(projectId, {
          name: newBucket.name,
          public: !newBucket.private,
          tier: selectedTier.name,
          billing_cycle: selectedCycle.key,
          price: calcPrice(selectedTier, selectedCycle),
        });
        handleFetchBucketStorages();
        setNewBucket({ name: '', private: false });
        setSelectedTier(null);
        setSelectedCycle(billingCycles[0]);
        toast.success(`${newBucket.name} bucket created successfully`);
        setIsOpen(false);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error creating bucket');
      } finally {
        setIsCreating(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newBucket.name, newBucket.private, selectedTier, selectedCycle],
  );

  useEffect(() => {
    handleFetchBucketStorages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="border-0 text-primaryBlue cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft size={20} />
          </Button>
          <p className="crimson-font text-lg font-medium text-black dark:text-white sm:text-xl">
            Bucket Storages
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <FiSearch className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search buckets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-primaryBlue focus:border-primaryBlue dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 outline-none transition-all"
            />
          </div>
          <Button
            className="w-full sm:w-auto py-2 px-3 cursor-pointer bg-[#1da1f1] border-0"
            size="sm"
            onClick={() => setIsOpen(!isOpen)}
          >
            <PlusIcon className="mr-2" />
            New Bucket
          </Button>
        </div>
      </div>

      {/* Bucket list / empty state */}
      {isFetchingBuckets ? (
        <ul className="grid grid-cols-2 gap-4 lg:grid-cols-5 mt-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <CardSkeleton key={i} containerHeight={15} />
          ))}
        </ul>
      ) : (
        <div className="mt-4">
          {hasBuckets ? (
            <ul className="grid grid-cols-2 gap-4 lg:grid-cols-5">
              {buckets
                .filter((b) =>
                  b.name.toLowerCase().includes(searchQuery.toLowerCase()),
                )
                .map((b) => (
                  <BucketItem key={b.name} item={b} projectId={projectId} />
                ))}
            </ul>
          ) : (
            <div className="flex h-72 flex-col items-center justify-center rounded-md border border-dashed border-gray-300 bg-white p-6 text-center">
              <GrStorage
                className="mb-3 h-10 w-10 text-gray-400"
                aria-hidden="true"
              />
              <p className="text-sm text-gray-600">
                No bucket, use the New Bucket button at the top right corner to
                create a bucket
              </p>
            </div>
          )}

          {/* ── Drawer ── */}
          <Drawer
            title="New Bucket"
            open={isOpen}
            onClose={() => setIsOpen(false)}
          >
            <form className="space-y-5" onSubmit={handleCreateBucket}>
              {/* Bucket name */}
              <Input
                label="Bucket Name"
                type="text"
                name="name"
                placeholder="Enter bucket name..."
                value={newBucket.name}
                onChange={handleChange}
                required
              />

              {/* Tier cards */}
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select a Tier
                </p>
                <div className="flex flex-col gap-2">
                  {tierList.map((tier) => (
                    <TierCard
                      key={tier.name}
                      tier={tier}
                      selected={selectedTier}
                      cycle={selectedCycle}
                      onSelect={setSelectedTier}
                    />
                  ))}
                </div>
              </div>

              {/* Price summary */}
              {selectedTier && (
                <div className="rounded-lg border border-[#1da1f1]/30 bg-[#1da1f1]/5 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Total (Monthly)</p>
                      <p className="text-base font-bold text-[#1da1f1]">
                        {formatPrice(calcPrice(selectedTier, selectedCycle))}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {selectedTier.name} · {selectedTier.gb} GB
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Private checkbox */}
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="private"
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    id="private"
                    name="private"
                    className="text-primaryBlue rounded-sm"
                    checked={newBucket.private}
                    onChange={handleChange}
                  />
                  Private
                </Label>
                <Tooltip content="Set to public by default" placement="right">
                  <BsInfoCircle
                    size={10}
                    className="cursor-pointer text-gray-400"
                  />
                </Tooltip>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setNewBucket(defaultValues);
                    setSelectedTier(null);
                    setIsOpen(false);
                  }}
                  className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedTier || isCreating || isBucketLimitReached}
                  onClick={(e) => {
                    if (isBucketLimitReached) {
                      e.preventDefault();
                      toast.warning("You have reached your limit of 25 Buckets. Please contact support to increase your limit.", { theme: "colored" });
                    }
                  }}
                  className={`rounded px-3 py-2 text-sm text-white transition-colors ${isBucketLimitReached ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#1da1f1] hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50'}`}
                >
                  {isCreating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </Drawer>
        </div>
      )}
    </div>
  );
};

export default BucketStorages;
